// What a content calendar shows in this browser: its view (the month, a week or the status board), whether
// the ideas column is out, the day whose month or week is in view, and the post open in the panel. The view
// and the ideas column are kept per board in this browser; the day starts at today on every visit.

import { create } from "zustand";
import { POST_STATUSES, dayKey, type PostStatus } from "../../../shared/calendar";
import type { ID } from "../../../shared/types";
import { readStoredJson, writeStored } from "../../lib/stored";

export type CalView = "month" | "week" | "status";

interface Kept {
  view: CalView;
  ideas: boolean;
  /** the statuses filtered out (David, 15 Oct); none at first */
  hide: PostStatus[];
}

interface CalUi {
  kept: Record<ID, Kept>;
  /** the day whose month (or week) is in view, per board, for this visit */
  at: Record<ID, string>;
  /** the post open in the panel, per board */
  open: Record<ID, ID | null>;
  /** a post being dragged, and where it would land */
  drag: ID | null;
  over: string | null;
  /** a post's own menu, opened by a right-click on it, where the pointer was */
  menu: { id: ID; x: number; y: number } | null;
}

const KEY = "cb:cal";
const DEFAULT: Kept = { view: "month", ideas: true, hide: [] };

function load(): Record<ID, Kept> {
  const d = readStoredJson<Record<ID, Partial<Kept>>>(KEY, {});
  const out: Record<ID, Kept> = {};
  for (const [id, k] of Object.entries(d)) out[id] = { view: k.view === "week" || k.view === "status" ? k.view : "month", ideas: k.ideas !== false, hide: Array.isArray(k.hide) ? POST_STATUSES.filter((s) => k.hide!.includes(s)) : [] };
  return out;
}

export const useCalUi = create<CalUi>(() => ({ kept: load(), at: {}, open: {}, drag: null, over: null, menu: null }));

/** A post's menu at the pointer, from a right-click on it; null closes it. */
export function postMenu(at: { id: ID; x: number; y: number } | null) {
  useCalUi.setState({ menu: at });
}

let saved = JSON.stringify(useCalUi.getState().kept);
useCalUi.subscribe((s) => {
  const next = JSON.stringify(s.kept);
  if (next === saved) return;
  saved = next;
  writeStored(KEY, next); // kept for this visit only when storage is out of reach
});

export const today = () => dayKey(new Date());

export function useKept(boardId: ID): Kept {
  return useCalUi((s) => s.kept[boardId] ?? DEFAULT);
}

export function setKept(boardId: ID, patch: Partial<Kept>) {
  useCalUi.setState((s) => ({ kept: { ...s.kept, [boardId]: { ...(s.kept[boardId] ?? DEFAULT), ...patch } } }));
}

export function useAt(boardId: ID): string {
  return useCalUi((s) => s.at[boardId]) ?? today();
}

export function setAt(boardId: ID, day: string) {
  useCalUi.setState((s) => ({ at: { ...s.at, [boardId]: day } }));
}

export function useOpen(boardId: ID): ID | null {
  return useCalUi((s) => s.open[boardId] ?? null);
}

export function openPost(boardId: ID, id: ID | null) {
  useCalUi.setState((s) => ({ open: { ...s.open, [boardId]: id } }));
}

export function setDrag(drag: ID | null, over: string | null = null) {
  useCalUi.setState({ drag, over });
}
